import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class PromotionTrackingService {
  private dbName = 'EnfafPromotionsDB';
  private dbVersion = 1;
  private storeName = 'viewedPromotions';
  private localStorageKey = 'enfaf_viewed_promotions';
  private useLocalStorage = false;

  constructor() {
    this.initDB().catch(() => {
      this.useLocalStorage = true;
    });
  }

  private async initDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!window.indexedDB) {
        reject(new Error('IndexedDB not supported'));
        return;
      }

      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => {
        reject(request.error);
      };

      request.onsuccess = () => {
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          const objectStore = db.createObjectStore(this.storeName, { keyPath: 'id' });
          objectStore.createIndex('promotionId', 'promotionId', { unique: false });
          objectStore.createIndex('userId', 'userId', { unique: false });
          objectStore.createIndex('viewedAt', 'viewedAt', { unique: false });
        }
      };
    });
  }

  private async getDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
    });
  }

  private getViewedPromotionsFromStorage(): Record<string, string[]> {
    try {
      const stored = localStorage.getItem(this.localStorageKey);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      return {};
    }
  }

  private saveViewedPromotionsToStorage(data: Record<string, string[]>): void {
    try {
      localStorage.setItem(this.localStorageKey, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  }

  private markPromotionAsViewedInStorage(promotionId: string, userId: string): void {
    const viewedPromotions = this.getViewedPromotionsFromStorage();
    if (!viewedPromotions[userId]) {
      viewedPromotions[userId] = [];
    }

    if (!viewedPromotions[userId].includes(promotionId)) {
      viewedPromotions[userId].push(promotionId);
      this.saveViewedPromotionsToStorage(viewedPromotions);
    }
  }

  private hasViewedPromotionInStorage(promotionId: string, userId: string): boolean {
    const viewedPromotions = this.getViewedPromotionsFromStorage();
    return viewedPromotions[userId]?.includes(promotionId) || false;
  }

  private clearAllViewedPromotionsInStorage(): void {
    localStorage.removeItem(this.localStorageKey);
  }

  async markPromotionAsViewed(promotionId: string, userId: string): Promise<void> {
    if (this.useLocalStorage) {
      this.markPromotionAsViewedInStorage(promotionId, userId);
      return;
    }

    try {
      const db = await this.getDB();
      const transaction = db.transaction([this.storeName], 'readwrite');
      const objectStore = transaction.objectStore(this.storeName);

      const viewedPromotion = {
        id: `${userId}_${promotionId}`,
        promotionId,
        userId,
        viewedAt: new Date().toISOString(),
        interacted: true
      };

      await new Promise<void>((resolve, reject) => {
        const request = objectStore.put(viewedPromotion);
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve();
      });
    } catch (error) {
      this.useLocalStorage = true;
      this.markPromotionAsViewedInStorage(promotionId, userId);
    }
  }

  async hasViewedPromotion(promotionId: string, userId: string): Promise<boolean> {
    if (this.useLocalStorage) {
      return this.hasViewedPromotionInStorage(promotionId, userId);
    }

    try {
      const db = await this.getDB();
      const transaction = db.transaction([this.storeName], 'readonly');
      const objectStore = transaction.objectStore(this.storeName);

      return new Promise<boolean>((resolve, reject) => {
        const request = objectStore.get(`${userId}_${promotionId}`);
        request.onerror = () => reject(request.error);
        request.onsuccess = () => {
          resolve(!!request.result);
        };
      });
    } catch (error) {
      this.useLocalStorage = true;
      return this.hasViewedPromotionInStorage(promotionId, userId);
    }
  }
}
